import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from '../../../../services/notificationService';
import {
  SampleMasterFormModal,
  ServiceMasterViewModal,
  ConfirmDialog,
  ModuleHeader
} from 'components/modals';
import type {
  ActionButton,
  ViewModeOption
} from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  SyutSearchGrid,
  MinimizedModalStrip
} from 'components/ui';
/**
 * ============================================================================
 * SERVICE MASTER SCREEN v2.0 - PRODUCTION READY
 * ============================================================================
 * 
 * NEW FEATURES IN v2.0:
 * âœ… Column Visibility Control (with default/optional columns)
 * âœ… Grid View with service cards
 * âœ… Calendar View (disabled/inactive)
 * âœ… Multiple BU/Entity selection in filters
 * âœ… Searchable Service Category dropdown
 * âœ… Searchable UOM dropdown
 * âœ… Similar service name suggestions
 * âœ… Enhanced validation rules (Entity→Enterprise, single tax at entity)
 * âœ… All previous features maintained
 * ============================================================================
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface ServiceMasterRecord {
  id: string;
  code: string;
  serviceName: string;
  description: string;
  category: string;
  entities: string[];
  uom: string;
  effectiveFrom: string;
  effectiveTo: string;
  status: 'Active' | 'Inactive';
  glAccount: string;
  taxDetails: TaxDetail[];
  scope: 'BU' | 'Entity' | 'Enterprise';
  createdAt: string;
  createdBy: string;
}

interface TaxDetail {
  id: string;
  taxType: string;
  taxCode: string;
  taxRate: string;
  jurisdiction: string;
}

interface ServiceCategory {
  id: string;
  name: string;
  defaultEntities: string[];
  defaultGLAccount: string;
  defaultTaxes: TaxDetail[];
}

interface GLAccount {
  id: string;
  code: string;
  name: string;
  category: string;
}

interface Entity {
  id: string;
  code: string;
  name: string;
  type: 'Entity' | 'Business Unit';
}

interface ColumnDefinition {
  id: string;
  label: string;
  field: string;
  required: boolean;
  defaultVisible: boolean;
  width?: number;
  sortable?: boolean;
}

type ViewMode = 'list' | 'grid' | 'calendar';

// ============================================================================
// SAMPLE DATA
// ============================================================================

const sampleCategories: ServiceCategory[] = [
  {
    id: 'cat1',
    name: 'Audit & Assurance Services',
    defaultEntities: ['entity1', 'bu1'],
    defaultGLAccount: 'gl1',
    defaultTaxes: [
      { id: 'tax1', taxType: 'GST', taxCode: 'HSN9978', taxRate: '18%', jurisdiction: 'India' }
    ]
  },
  {
    id: 'cat2',
    name: 'Tax Advisory Services',
    defaultEntities: ['entity1', 'entity2'],
    defaultGLAccount: 'gl2',
    defaultTaxes: [
      { id: 'tax2', taxType: 'GST', taxCode: 'HSN9983', taxRate: '18%', jurisdiction: 'India' }
    ]
  },
  {
    id: 'cat3',
    name: 'Financial Advisory Services',
    defaultEntities: ['entity1'],
    defaultGLAccount: 'gl3',
    defaultTaxes: [
      { id: 'tax3', taxType: 'VAT', taxCode: 'VAT5', taxRate: '5%', jurisdiction: 'Botswana' }
    ]
  }
];

const sampleEntities: Entity[] = [
  { id: 'entity1', code: 'E001', name: 'Sample Entity A', type: 'Entity' },
  { id: 'entity2', code: 'E002', name: 'Sample Entity B', type: 'Entity' },
  { id: 'entity3', code: 'E003', name: 'Test Entity 1', type: 'Entity' },
  { id: 'bu1', code: 'BU001', name: 'Sample Business Unit 1', type: 'Business Unit' },
  { id: 'bu2', code: 'BU002', name: 'Sample Business Unit 2', type: 'Business Unit' },
  { id: 'bu3', code: 'BU003', name: 'Test Business Unit 1', type: 'Business Unit' }
];

const sampleGLAccounts: GLAccount[] = [
  { id: 'gl1', code: '4001', name: 'Audit Revenue', category: 'Revenue' },
  { id: 'gl2', code: '4002', name: 'Tax Advisory Revenue', category: 'Revenue' },
  { id: 'gl3', code: '4003', name: 'Financial Advisory Revenue', category: 'Revenue' },
  { id: 'gl4', code: '4004', name: 'Consulting Revenue', category: 'Revenue' },
  { id: 'gl5', code: '4005', name: 'Other Professional Revenue', category: 'Revenue' }
];

const sampleUOMs: string[] = ['Hour', 'Day', 'Week', 'Month', 'Job', 'Project', 'Deliverable'];

const sampleTaxServices: TaxDetail[] = [
  { id: 'tax1', taxType: 'GST', taxCode: 'HSN9978', taxRate: '18%', jurisdiction: 'India' },
  { id: 'tax2', taxType: 'GST', taxCode: 'HSN9983', taxRate: '18%', jurisdiction: 'India' },
  { id: 'tax3', taxType: 'GST', taxCode: 'HSN9985', taxRate: '12%', jurisdiction: 'India' },
  { id: 'tax4', taxType: 'VAT', taxCode: 'VAT5', taxRate: '5%', jurisdiction: 'Botswana' }
];

// Helper function to generate 50 sample records per tab
const generateSampleRecords = (
  tabPrefix: string,
  startId: number,
  category: string,
  glAccountId: string,
  taxDetail: TaxDetail
): ServiceMasterRecord[] => {
  const serviceNames = [
    'Statutory Audit', 'Internal Audit', 'Tax Compliance', 'GST Advisory', 'Financial Due Diligence',
    'Valuation Services', 'Risk Assessment', 'Compliance Review', 'Process Audit', 'IT Audit',
    'Forensic Audit', 'Revenue Audit', 'Cost Audit', 'Stock Audit', 'Bank Audit',
    'Transfer Pricing', 'Direct Tax Advisory', 'Indirect Tax Advisory', 'Tax Planning', 'Tax Litigation',
    'M&A Advisory', 'IPO Advisory', 'Debt Restructuring', 'Capital Raising', 'Business Valuation',
    'Intangible Valuation', 'Fair Value Assessment', 'Purchase Price Allocation', 'Impairment Testing', 'Financial Modeling',
    'Budgeting Services', 'Forecasting Services', 'Cash Flow Analysis', 'Working Capital Management', 'Treasury Services',
    'Payroll Processing', 'Bookkeeping Services', 'Accounts Receivable', 'Accounts Payable', 'General Ledger',
    'Management Reporting', 'Dashboard Development', 'KPI Tracking', 'Variance Analysis', 'Profitability Analysis',
    'Regulatory Compliance', 'SOX Compliance', 'GDPR Compliance', 'ESG Reporting', 'Sustainability Audit'
  ];

  const uoms = ['Hour', 'Day', 'Week', 'Month', 'Job', 'Project', 'Deliverable'];
  const scopes: ('BU' | 'Entity' | 'Enterprise')[] = ['BU', 'Entity', 'Enterprise'];
  const statuses: ('Active' | 'Inactive')[] = ['Active', 'Active', 'Active', 'Active', 'Inactive'];
  const entityCombinations = [
    ['entity1'], ['entity2'], ['entity1', 'entity2'], ['entity1', 'bu1'],
    ['entity2', 'bu2'], ['entity1', 'entity2', 'bu1'], ['bu1', 'bu2'], ['entity1', 'bu3']
  ];

  return Array.from({ length: 50 }, (_, i) => ({
    id: `${tabPrefix}-${startId + i}`,
    code: `${tabPrefix}${String(startId + i).padStart(3, '0')}`,
    serviceName: `${serviceNames[i % serviceNames.length]} ${tabPrefix}`,
    description: `${serviceNames[i % serviceNames.length]} services for ${category}`,
    category: category,
    entities: entityCombinations[i % entityCombinations.length],
    uom: uoms[i % uoms.length],
    effectiveFrom: '2024-01-01',
    effectiveTo: i % 10 === 0 ? '2024-12-31' : '',
    status: statuses[i % statuses.length],
    glAccount: glAccountId,
    taxDetails: [taxDetail],
    scope: scopes[i % scopes.length],
    createdAt: '2024-01-01',
    createdBy: 'Admin'
  }));
};

// Tab configuration
interface TabConfig {
  id: string;
  label: string;
  category: string;
  glAccountId: string;
  taxDetail: TaxDetail;
  prefix: string;
}

const tabConfigs: TabConfig[] = [
  {
    id: 'audit',
    label: 'Audit Services',
    category: 'Audit & Assurance Services',
    glAccountId: 'gl1',
    taxDetail: sampleTaxServices[0],
    prefix: 'AUD'
  },
  {
    id: 'tax',
    label: 'Tax Services',
    category: 'Tax Advisory Services',
    glAccountId: 'gl2',
    taxDetail: sampleTaxServices[1],
    prefix: 'TAX'
  },
  {
    id: 'financial',
    label: 'Financial Advisory',
    category: 'Financial Advisory Services',
    glAccountId: 'gl3',
    taxDetail: sampleTaxServices[2],
    prefix: 'FIN'
  },
  {
    id: 'consulting',
    label: 'Consulting',
    category: 'Consulting Services',
    glAccountId: 'gl4',
    taxDetail: sampleTaxServices[3],
    prefix: 'CON'
  }
];

// Generate data for each tab (50 records each)
const tabDataMap: Record<string, ServiceMasterRecord[]> = {
  audit: generateSampleRecords('AUD', 1, 'Audit & Assurance Services', 'gl1', sampleTaxServices[0]),
  tax: generateSampleRecords('TAX', 51, 'Tax Advisory Services', 'gl2', sampleTaxServices[1]),
  financial: generateSampleRecords('FIN', 101, 'Financial Advisory Services', 'gl3', sampleTaxServices[2]),
  consulting: generateSampleRecords('CON', 151, 'Consulting Services', 'gl4', sampleTaxServices[3])
};

// Legacy: All services combined for backwards compatibility
const sampleServices: ServiceMasterRecord[] = Object.values(tabDataMap).flat();

// ============================================================================
// COLUMN DEFINITIONS (v2.0 - NEW)
// ============================================================================

const columnDefinitions: ColumnDefinition[] = [
  {
    id: 'select',
    label: '',
    field: 'select',
    required: true,
    defaultVisible: true,
    width: 50,
    sortable: false
  },
  {
    id: 'code',
    label: 'Code',
    field: 'code',
    required: false,
    defaultVisible: true, // DEFAULT
    sortable: true
  },
  {
    id: 'serviceName',
    label: 'Service Name',
    field: 'serviceName',
    required: false,
    defaultVisible: true, // DEFAULT
    sortable: true
  },
  {
    id: 'description',
    label: 'Description',
    field: 'description',
    required: false,
    defaultVisible: true, // DEFAULT
    sortable: false
  },
  {
    id: 'category',
    label: 'Category',
    field: 'category',
    required: false,
    defaultVisible: true, // DEFAULT
    sortable: true
  },
  {
    id: 'uom',
    label: 'UOM',
    field: 'uom',
    required: false,
    defaultVisible: true, // DEFAULT
    sortable: true
  },
  {
    id: 'status',
    label: 'Status',
    field: 'status',
    required: false,
    defaultVisible: true, // DEFAULT
    sortable: true
  },
  {
    id: 'entities',
    label: 'Entities',
    field: 'entities',
    required: false,
    defaultVisible: false, // OPTIONAL
    sortable: false
  },
  {
    id: 'effectiveFrom',
    label: 'Effective From',
    field: 'effectiveFrom',
    required: false,
    defaultVisible: false, // OPTIONAL
    sortable: true
  },
  {
    id: 'effectiveTo',
    label: 'Effective To',
    field: 'effectiveTo',
    required: false,
    defaultVisible: false, // OPTIONAL
    sortable: true
  },
  {
    id: 'glAccount',
    label: 'GL Account',
    field: 'glAccount',
    required: false,
    defaultVisible: false, // OPTIONAL
    sortable: true
  },
  {
    id: 'actions',
    label: 'Actions',
    field: 'actions',
    required: true,
    defaultVisible: true,
    width: 80,
    sortable: false
  }
];

// ============================================================================
// COLUMN SELECTOR COMPONENT (v2.0 - NEW)
// ============================================================================

interface ColumnSelectorProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onApply: (visibleColumns: string[]) => void;
  onReset: () => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleColumns: initialVisible,
  onApply,
  onReset
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialVisible);
  const [isMobile, setIsMobile] = useState(false);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedColumns(initialVisible);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, initialVisible]);

  const toggleColumn = (columnId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (column?.required) return;

    setSelectedColumns(prev =>
      prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  const handleSelectAll = () => {
    const allColumnIds = columns
      .filter(col => !col.required)
      .map(col => col.id);
    
    const requiredIds = columns
      .filter(col => col.required)
      .map(col => col.id);

    if (selectedColumns.length === columns.length) {
      setSelectedColumns(requiredIds);
    } else {
      setSelectedColumns([...requiredIds, ...allColumnIds]);
    }
  };

  const handleReset = () => {
    setConfirmResetOpen(true);
  };

  const handleConfirmReset = () => {
    onReset();
    setConfirmResetOpen(false);
    setIsOpen(false);
  };

  const handleApply = () => {
    onApply(selectedColumns);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setSelectedColumns(initialVisible);
    setIsOpen(false);
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={panelRef}>
      <button
        className={`btn-icon ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Manage Columns"
        style={isOpen ? {
          background: 'var(--accent-gradient)',
          color: 'white',
          borderColor: 'var(--primary)'
        } : {}}
      >
        <IconSystem.grid size={18} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          {isMobile && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                zIndex: 999
              }}
              onClick={() => setIsOpen(false)}
            />
          )}

          <div style={{
            position: isMobile ? 'fixed' : 'absolute',
            top: isMobile ? '10%' : 'calc(100% + 0.5rem)',
            left: isMobile ? '50%' : 'auto',
            right: isMobile ? 'auto' : 0,
            transform: isMobile ? 'translateX(-50%)' : 'none',
            width: isMobile ? 'calc(100vw - 2rem)' : '320px',
            maxWidth: '320px',
            maxHeight: isMobile ? 'calc(90vh - 2rem)' : '500px',
            background: 'var(--bg-primary)',
            border: '2px solid var(--primary)',
            borderRadius: '0.75rem',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            animation: 'slideDown 0.2s ease',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
          {/* Header */}
          <div style={{
            padding: '1rem 1.25rem',
            borderBottom: '2px solid var(--border-color)',
            background: 'var(--bg-secondary)',
            borderRadius: '0.75rem 0.75rem 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0
          }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
              Manage Columns
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                width: '1.5rem',
                height: '1.5rem',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '0.25rem'
              }}
            >
              <IconSystem.x size={16} />
            </button>
          </div>

          {/* Content */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '0.75rem 0',
            minHeight: 0
          }}>
            {columns.map(column => (
              <div
                key={column.id}
                onClick={() => toggleColumn(column.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.625rem 1.25rem',
                  cursor: column.required ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  gap: '0.75rem',
                  opacity: column.required ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!column.required) {
                    e.currentTarget.style.background = '#f8fafc';
                    e.currentTarget.style.paddingLeft = '1.5rem';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.paddingLeft = '1.25rem';
                }}
              >
                <div style={{
                  width: '1.125rem',
                  height: '1.125rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid var(--border-color)',
                  borderRadius: '0.25rem',
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                  ...(selectedColumns.includes(column.id) ? {
                    background: 'var(--accent-gradient)',
                    borderColor: 'var(--primary)',
                    color: 'white'
                  } : {})
                }}>
                  {selectedColumns.includes(column.id) && <IconSystem.check size={14} />}
                </div>
                <span style={{
                  flex: 1,
                  fontSize: '0.85rem',
                  color: 'var(--text-primary)',
                  userSelect: 'none'
                }}>
                  {column.label}{column.required && ' *'}
                </span>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div style={{
            padding: '1rem',
            borderTop: '2px solid var(--border-color)',
            background: 'var(--bg-primary)',
            boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleSelectAll}
                style={{
                  flex: 1,
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.75rem',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.5rem',
                  cursor: 'pointer'
                }}
              >
                {selectedColumns.length === columns.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={handleReset}
                style={{
                  flex: 1,
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.75rem',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.5rem',
                  cursor: 'pointer'
                }}
              >
                Reset Default
              </button>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCancel}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                className="btn btn-primary"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </>
      )}

      <ConfirmDialog
        open={confirmResetOpen}
        title="Reset Columns"
        message="Reset to default column configuration?"
        confirmLabel="Reset"
        cancelLabel="Cancel"
        onConfirm={handleConfirmReset}
        onCancel={() => setConfirmResetOpen(false)}
      />
    </div>
  );
};

// ============================================================================
// GRID VIEW CARD COMPONENT (v2.0 - NEW)
// ============================================================================

interface ServiceCardProps {
  service: ServiceMasterRecord;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ service, onView, onEdit, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const glAccount = sampleGLAccounts.find(gl => gl.id === service.glAccount);
  const primaryTax = service.taxDetails[0];

  return (
    <div style={{
      background: 'var(--bg-primary)',
      border: '1px solid var(--border-color)',
      borderRadius: '0.75rem',
      padding: '1.25rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.2s ease',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem'
    }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'var(--text-secondary)',
            marginBottom: '0.25rem'
          }}>
            {service.code}
          </div>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: 700,
            color: 'var(--text-primary)',
            margin: 0,
            marginBottom: '0.5rem'
          }}>
            {service.serviceName}
          </h3>
        </div>
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            className={`action-menu-trigger ${menuOpen ? 'active' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            style={{ marginTop: '-0.25rem' }}
          >
            <IconSystem.moreVertical size={16} />
          </button>
          {menuOpen && (
            <div className="action-menu-dropdown" style={{ right: 0, top: 'calc(100% + 0.5rem)' }}>
              <button className="action-menu-item" onClick={() => { onView(service.id); setMenuOpen(false); }}>
                <IconSystem.eye size={18} />
                <span>View Details</span>
              </button>
              <button className="action-menu-item" onClick={() => { onEdit(service.id); setMenuOpen(false); }}>
                <IconSystem.edit size={18} />
                <span>Edit</span>
              </button>
              <button className="action-menu-item" onClick={() => { onEdit(service.id); setMenuOpen(false); }}>
                <IconSystem.copy size={18} />
                <span>Copy</span>
              </button>
              <div className="action-menu-divider" />
              <button className="action-menu-item action-menu-item-danger" onClick={() => { onDelete(service.id); setMenuOpen(false); }}>
                <IconSystem.trash size={18} />
                <span>Delete</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      {service.description && (
        <p style={{
          fontSize: '0.85rem',
          color: 'var(--text-secondary)',
          margin: 0,
          lineHeight: 1.5,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {service.description}
        </p>
      )}

      {/* Details Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid rgba(226, 232, 240, 0.5)'
      }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Category</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>{service.category}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>UOM</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>{service.uom}</div>
        </div>
        {primaryTax && (
          <div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Tax</div>
            <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>
              {primaryTax.taxCode} - {primaryTax.taxRate}
            </div>
          </div>
        )}
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Status</div>
          <span className={`badge badge-${service.status.toLowerCase()}`}>
            {service.status}
          </span>
        </div>
      </div>

      {/* Footer */}
      {glAccount && (
        <div style={{
          paddingTop: '0.75rem',
          borderTop: '1px solid rgba(226, 232, 240, 0.5)',
          fontSize: '0.75rem',
          color: '#64748b'
        }}>
          <strong>GL Account:</strong> {glAccount.code} - {glAccount.name}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// RESPONSIVE TABS COMPONENT
// ============================================================================

interface ResponsiveTabsProps {
  tabs: TabConfig[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  tabCounts?: Record<string, number>;
}

const ResponsiveTabs: React.FC<ResponsiveTabsProps> = ({
  tabs,
  activeTab,
  onTabChange,
  tabCounts = {}
}) => {
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [visibleTabsCount, setVisibleTabsCount] = useState(tabs.length);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  // Calculate how many tabs can fit in the visible area
  useEffect(() => {
    const calculateVisibleTabs = () => {
      const containerWidth = tabsContainerRef.current?.parentElement?.offsetWidth || 0;
      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        // On mobile, show only 2 tabs + more button
        setVisibleTabsCount(2);
      } else if (containerWidth < 600) {
        setVisibleTabsCount(2);
      } else if (containerWidth < 800) {
        setVisibleTabsCount(3);
      } else {
        setVisibleTabsCount(tabs.length);
      }
    };

    calculateVisibleTabs();
    window.addEventListener('resize', calculateVisibleTabs);
    return () => window.removeEventListener('resize', calculateVisibleTabs);
  }, [tabs.length]);

  // Close more menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setMoreMenuOpen(false);
      }
    };

    if (moreMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [moreMenuOpen]);

  const visibleTabs = tabs.slice(0, visibleTabsCount);
  const overflowTabs = tabs.slice(visibleTabsCount);
  const hasOverflow = overflowTabs.length > 0;

  // Check if active tab is in overflow
  const activeTabInOverflow = overflowTabs.some(tab => tab.id === activeTab);

  return (
    <div className="responsive-tabs-container" style={{
      display: 'flex',
      alignItems: 'center',
      borderBottom: '2px solid var(--border-color)',
      background: 'var(--bg-primary)',
      position: 'relative'
    }}>
      <style>{`
        .responsive-tabs-container {
          gap: 0;
        }

        .tab-item {
          padding: 0.875rem 1.25rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--text-secondary);
          background: transparent;
          border: none;
          cursor: pointer;
          position: relative;
          transition: all 0.2s ease;
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .tab-item:hover {
          color: var(--primary);
          background: var(--bg-secondary);
        }

        .tab-item.active {
          color: var(--primary);
          font-weight: 600;
        }

        .tab-item.active::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          right: 0;
          height: 3px;
          background: var(--accent-gradient);
          border-radius: 3px 3px 0 0;
        }

        .tab-count {
          font-size: 0.7rem;
          padding: 0.125rem 0.5rem;
          background: var(--bg-secondary);
          border-radius: 10px;
          color: var(--text-muted);
        }

        .tab-item.active .tab-count {
          background: rgba(37, 99, 235, 0.1);
          color: var(--primary);
        }

        .more-tabs-button {
          padding: 0.875rem 1rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--text-secondary);
          background: transparent;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.375rem;
          transition: all 0.2s ease;
        }

        .more-tabs-button:hover {
          color: var(--primary);
          background: var(--bg-secondary);
        }

        .more-tabs-button.has-active {
          color: var(--primary);
        }

        .more-tabs-button.has-active::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          right: 0;
          height: 3px;
          background: var(--accent-gradient);
          border-radius: 3px 3px 0 0;
        }

        .more-tabs-dropdown {
          position: absolute;
          top: calc(100% + 4px);
          right: 0;
          min-width: 200px;
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 0.5rem;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
          z-index: 1000;
          padding: 0.5rem 0;
          animation: slideDown 0.2s ease;
        }

        .more-tab-item {
          width: 100%;
          padding: 0.75rem 1rem;
          font-size: 0.875rem;
          color: var(--text-secondary);
          background: transparent;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          transition: all 0.15s ease;
          text-align: left;
        }

        .more-tab-item:hover {
          background: var(--bg-secondary);
          color: var(--primary);
        }

        .more-tab-item.active {
          background: rgba(37, 99, 235, 0.08);
          color: var(--primary);
          font-weight: 600;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 768px) {
          .tab-item {
            padding: 0.75rem 1rem;
            font-size: 0.8rem;
          }

          .tab-count {
            font-size: 0.65rem;
            padding: 0.1rem 0.375rem;
          }
        }
      `}</style>

      {/* Visible Tabs */}
      <div ref={tabsContainerRef} style={{ display: 'flex', overflow: 'hidden' }}>
        {visibleTabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-item ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => onTabChange(tab.id)}
          >
            {tab.label}
            {tabCounts[tab.id] !== undefined && (
              <span className="tab-count">{tabCounts[tab.id]}</span>
            )}
          </button>
        ))}
      </div>

      {/* More Button (3 dots) */}
      {hasOverflow && (
        <div ref={moreMenuRef} style={{ position: 'relative' }}>
          <button
            className={`more-tabs-button ${activeTabInOverflow ? 'has-active' : ''}`}
            onClick={() => setMoreMenuOpen(!moreMenuOpen)}
            title="More tabs"
            style={{ position: 'relative' }}
          >
            <IconSystem.moreHorizontal size={18} />
            <span style={{ fontSize: '0.8rem' }}>More</span>
            <IconSystem.chevronDown size={14} style={{
              transform: moreMenuOpen ? 'rotate(180deg)' : 'rotate(0)',
              transition: 'transform 0.2s ease'
            }} />
          </button>

          {moreMenuOpen && (
            <div className="more-tabs-dropdown">
              {overflowTabs.map(tab => (
                <button
                  key={tab.id}
                  className={`more-tab-item ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => {
                    onTabChange(tab.id);
                    setMoreMenuOpen(false);
                  }}
                >
                  <span>{tab.label}</span>
                  {tabCounts[tab.id] !== undefined && (
                    <span className="tab-count">{tabCounts[tab.id]}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN SERVICE MASTER SCREEN COMPONENT
// ============================================================================

interface SampleMasterScreenTabbedProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const SampleMasterScreenTabbed: React.FC<SampleMasterScreenTabbedProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // Tab state - NEW
  const [activeTab, setActiveTab] = useState<string>(tabConfigs[0].id);

  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [gridPage, setGridPage] = useState(0);
  const [gridPageSize, setGridPageSize] = useState(25);

  // Column visibility state (v2.0 - NEW)
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const defaultVisible = columnDefinitions
      .filter(col => col.defaultVisible)
      .map(col => col.id);
    return defaultVisible;
  });

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [codeFilter, setCodeFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [descriptionFilter, setDescriptionFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [uomFilter, setUomFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState<string[]>([]);
  const [effectiveFromFilter, setEffectiveFromFilter] = useState('');
  const [effectiveToFilter, setEffectiveToFilter] = useState('');
  const [glAccountFilter, setGlAccountFilter] = useState('');

  // Selection state
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [activeMenuRowId, setActiveMenuRowId] = useState<string | null>(null);

  // Modal states
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchPopupOpen, setSearchPopupOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<ServiceMasterRecord | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const copyInstanceCounter = useRef(0);
  const [copyInstanceId, setCopyInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);
  // Get current tab data
  const currentTabData = useMemo(() =>
    tabDataMap[activeTab] || [],
    [activeTab]
  );

  // Tab counts for display
  const tabCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    tabConfigs.forEach(tab => {
      const data = tabDataMap[tab.id] || [];
      // Apply filters to get count
      counts[tab.id] = data.filter(service => {
        const matchesSearch = !searchQuery ||
          service.serviceName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          service.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
          service.description.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCode = codeFilter === '' || service.code.toLowerCase().includes(codeFilter.toLowerCase());
        const matchesName = nameFilter === '' || service.serviceName.toLowerCase().includes(nameFilter.toLowerCase());
        const matchesDescription = descriptionFilter === '' || service.description.toLowerCase().includes(descriptionFilter.toLowerCase());
        const matchesUom = uomFilter === '' || service.uom === uomFilter;
        const matchesStatus = statusFilter === '' || service.status === statusFilter;
        const matchesEntity = entityFilter.length === 0 ||
          service.entities.some(e => entityFilter.includes(e));
        const matchesEffectiveFrom = effectiveFromFilter === '' || service.effectiveFrom >= effectiveFromFilter;
        const matchesEffectiveTo = effectiveToFilter === '' || (service.effectiveTo !== '' && service.effectiveTo <= effectiveToFilter);
        const matchesGlAccount = glAccountFilter === '' || service.glAccount === glAccountFilter;
        return matchesSearch && matchesCode && matchesName && matchesDescription && matchesUom && matchesStatus && matchesEntity && matchesEffectiveFrom && matchesEffectiveTo && matchesGlAccount;
      }).length;
    });
    return counts;
  }, [searchQuery, codeFilter, nameFilter, descriptionFilter, uomFilter, statusFilter, entityFilter, effectiveFromFilter, effectiveToFilter, glAccountFilter]);

  // Filtered data - Now based on active tab
  const filteredData = useMemo(() =>
    currentTabData.filter(service => {
      const matchesSearch =
        service.serviceName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        service.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        service.description.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCode = codeFilter === '' || service.code.toLowerCase().includes(codeFilter.toLowerCase());
      const matchesName = nameFilter === '' || service.serviceName.toLowerCase().includes(nameFilter.toLowerCase());
      const matchesDescription = descriptionFilter === '' || service.description.toLowerCase().includes(descriptionFilter.toLowerCase());
      const matchesCategory = categoryFilter === '' || service.category === categoryFilter;
      const matchesUom = uomFilter === '' || service.uom === uomFilter;
      const matchesStatus = statusFilter === '' || service.status === statusFilter;
      const matchesEntity = entityFilter.length === 0 ||
        service.entities.some(e => entityFilter.includes(e));
      const matchesEffectiveFrom = effectiveFromFilter === '' || service.effectiveFrom >= effectiveFromFilter;
      const matchesEffectiveTo = effectiveToFilter === '' || (service.effectiveTo !== '' && service.effectiveTo <= effectiveToFilter);
      const matchesGlAccount = glAccountFilter === '' || service.glAccount === glAccountFilter;

      return matchesSearch && matchesCode && matchesName && matchesDescription && matchesCategory && matchesUom && matchesStatus && matchesEntity && matchesEffectiveFrom && matchesEffectiveTo && matchesGlAccount;
    }),
    [currentTabData, searchQuery, codeFilter, nameFilter, descriptionFilter, categoryFilter, uomFilter, statusFilter, entityFilter, effectiveFromFilter, effectiveToFilter, glAccountFilter]
  );

  // Reset selection when tab changes
  useEffect(() => {
    setSelectedRows([]);
  }, [activeTab]);

  // Column visibility handlers (v2.0 - NEW)
  const handleApplyColumns = useCallback((newVisibleColumns: string[]) => {
    setVisibleColumns(newVisibleColumns);
    // Save to localStorage
    localStorage.setItem('service-master-columns', JSON.stringify(newVisibleColumns));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaultVisible = columnDefinitions
      .filter(col => col.defaultVisible)
      .map(col => col.id);
    setVisibleColumns(defaultVisible);
    localStorage.removeItem('service-master-columns');
  }, []);

  // Load saved column preferences on mount
  useEffect(() => {
    const saved = localStorage.getItem('service-master-columns');
    if (saved) {
      try {
        setVisibleColumns(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load column preferences');
      }
    }
  }, []);

  // F2 keyboard shortcut to open search popup
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2' && !searchPopupOpen && !formModalOpen && !viewModalOpen && !deleteModalOpen) {
        e.preventDefault();
        setSearchPopupOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchPopupOpen, formModalOpen, viewModalOpen, deleteModalOpen]);

  // SyutGrid table columns definition
  const tableColumns: SyutGridColumn<ServiceMasterRecord>[] = useMemo(() => [
    {
      id: 'code',
      header: 'Code',
      accessorKey: 'code',
      size: 100,
    },
    {
      id: 'serviceName',
      header: 'Service Name',
      accessorKey: 'serviceName',
      size: 200,
    },
    {
      id: 'description',
      header: 'Description',
      accessorKey: 'description',
      size: 250,
    },
    {
      id: 'category',
      header: 'Category',
      accessorKey: 'category',
      size: 180,
    },
    {
      id: 'uom',
      header: 'UOM',
      accessorKey: 'uom',
      size: 80,
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      size: 100,
      cell: ({ getValue }) => {
        const status = getValue() as string;
        return (
          <span className={`badge badge-${status.toLowerCase()}`}>
            {status}
          </span>
        );
      },
    },
    {
      id: 'entities',
      header: 'Entities',
      accessorKey: 'entities',
      size: 200,
      cell: ({ getValue }) => {
        const entityIds = getValue() as string[];
        const entitiesDisplay = entityIds.map(eid => {
          const entity = sampleEntities.find(e => e.id === eid);
          return entity?.name || eid;
        }).join(', ');
        return <span title={entitiesDisplay}>{entitiesDisplay}</span>;
      },
    },
    {
      id: 'effectiveFrom',
      header: 'Effective From',
      accessorKey: 'effectiveFrom',
      size: 120,
    },
    {
      id: 'effectiveTo',
      header: 'Effective To',
      accessorKey: 'effectiveTo',
      size: 120,
      cell: ({ getValue }) => {
        const value = getValue() as string;
        return value || '-';
      },
    },
    {
      id: 'glAccount',
      header: 'GL Account',
      accessorKey: 'glAccount',
      size: 180,
      cell: ({ getValue }) => {
        const glId = getValue() as string;
        const glAccount = sampleGLAccounts.find(gl => gl.id === glId);
        return glAccount ? `${glAccount.code} - ${glAccount.name}` : '-';
      },
    },
  ], []);

  // Restore minimized form modal with saved form state
  const handleRestoreModal = useCallback((restoredMode: 'create' | 'edit' | 'copy', instanceId?: string, serviceCode?: string) => {
    if (instanceId) {
      if (restoredMode === 'copy') setCopyInstanceId(instanceId);
      else setCreateInstanceId(instanceId);
    }
    if (serviceCode) {
      const svc = sampleServices.find(s => s.code === serviceCode);
      if (svc) setSelectedService(svc);
    }
    setFormMode(restoredMode);
    setFormModalOpen(true);
  }, []);

  // Restore minimized view modal for a specific service code
  const handleRestoreView = useCallback((serviceCode: string) => {
    const service = sampleServices.find(s => s.code === serviceCode);
    if (service) {
      setSelectedService(service);
      setViewModalOpen(true);
    }
  }, []);

  const handleView = useCallback((id: string) => {
    const service = sampleServices.find(s => s.id === id);
    if (!service) return;
    const viewId = tabId ? `${tabId}-view-modal-${service.code}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId);
      modal?.onRestore?.();
      return;
    }
    setSelectedService(service);
    setViewModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleEdit = useCallback((id: string) => {
    const service = sampleServices.find(s => s.id === id);
    if (service) {
      const modalId = tabId ? `${tabId}-form-modal-edit-${service.code}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
    }
    if (service) {
      setFormMode('edit');
      setSelectedService(service);
      setFormModalOpen(true);
    }
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleCopy = useCallback((id: string) => {
    const service = sampleServices.find(s => s.id === id);
    if (service) {
      // Auto-minimize the currently open form modal before opening a new copy
      if (formModalOpen) {
        formModalMinimizeRef.current?.();
      }
      const newId = String(++copyInstanceCounter.current);
      setCopyInstanceId(newId);
      setFormMode('copy');
      setSelectedService(service);
      setFormModalOpen(true);
    }
  }, [formModalOpen]);

  const handleDelete = useCallback((id: string) => {
    const service = sampleServices.find(s => s.id === id);
    setSelectedService(service || null);
    setDeleteModalOpen(true);
  }, []);

  // Row actions for SyutGrid
  const getRowActions = useCallback((service: ServiceMasterRecord): ActionMenuItem[] => [
    {
      id: 'view',
      label: 'View Details',
      icon: <IconSystem.eye size={18} />,
      onClick: () => handleView(service.id),
    },
    {
      id: 'edit',
      label: 'Edit',
      icon: <IconSystem.edit size={18} />,
      onClick: () => handleEdit(service.id),
    },
    {
      id: 'copy',
      label: 'Copy',
      icon: <IconSystem.copy size={18} />,
      onClick: () => handleCopy(service.id),
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: <IconSystem.trash size={18} />,
      onClick: () => handleDelete(service.id),
      variant: 'danger',
    },
  ], [handleView, handleEdit, handleCopy, handleDelete]);

  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setFormMode('create');
    setSelectedService(null);
    setFormModalOpen(true);
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedRows.length === filteredData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredData.map(s => s.id));
    }
  }, [selectedRows.length, filteredData]);

  // Define view mode options
  const viewModeOptions: ViewModeOption[] = [
    {
      id: 'list',
      icon: <IconSystem.list size={16} />,
      label: 'List View',
      tooltip: 'List View'
    },
    {
      id: 'grid',
      icon: <IconSystem.grid size={16} />,
      label: 'Grid View',
      tooltip: 'Grid View'
    },
    {
      id: 'calendar',
      icon: <IconSystem.calendar size={16} />,
      label: 'Calendar View',
      tooltip: 'Calendar View (Not Applicable)',
      disabled: true
    }
  ];

  // Define action buttons
  const actionButtons: ActionButton[] = [
    {
      id: 'search',
      icon: <IconSystem.search size={18} />,
      label: 'Search Services',
      onClick: () => setSearchPopupOpen(true),
      tooltip: 'Search Services (F2)',
      variant: 'icon',
      visible: true
    },
    {
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'Add New Master',
      onClick: handleAddNew,
      tooltip: 'Add New Master',
      variant: 'icon',
      visible: true
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => {
        //throw new Error("SOMETHING WRONG")
        console.log('Refresh clicked')
      },
      tooltip: 'Refresh',
      variant: 'icon'
    },
    {
      id: 'export',
      icon: <IconSystem.download size={18} />,
      label: 'Export',
      onClick: () => console.log('Export clicked'),
      tooltip: 'Export',
      variant: 'icon'
    }
  ];

  return (
    <div className="module-master-container">
      {/* Module Header - Now using ModuleHeader component */}
      <ModuleHeader
        visible={true}
        title="Sample Master Definition"
        description="Catalogue of all services offered by the enterprise"
        icon={moduleIcon && (IconSystem as any)[moduleIcon]
          ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
          : undefined
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search services...'
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode as ViewMode)
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      {/* Module Content */}
      <div className="module-content" style={{ marginTop: '6px' }}>
        {/* Data Grid Container */}
        <div className="data-grid-container">
          {/* Responsive Tabs - At top of list section */}
          <ResponsiveTabs
            tabs={tabConfigs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            tabCounts={tabCounts}
          />

          {/* LIST VIEW */}
          {viewMode === 'list' && (
            <SyutGrid
              data={filteredData}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              rowActions={getRowActions}
              emptyMessage="No services found"
              frozenLeftColumns={['select', 'code']}
              frozenRightColumns={['actions']}
              visibleColumns={visibleColumns}
            />
          )}

          {/* v2.0 - GRID VIEW */}
          {viewMode === 'grid' && (() => {
            const totalPages = Math.max(1, Math.ceil(filteredData.length / gridPageSize));
            const safeGridPage = Math.min(gridPage, totalPages - 1);
            const pagedData = filteredData.slice(safeGridPage * gridPageSize, (safeGridPage + 1) * gridPageSize);
            return (
              <div className="syut-grid-component" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <div style={{
                  padding: '1rem',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: '1.5rem',
                  flex: 1,
                }}>
                  {pagedData.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      <IconSystem.grid size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                      <p>No services found</p>
                    </div>
                  ) : (
                    pagedData.map(service => (
                      <ServiceCard
                        key={service.id}
                        service={service}
                        onView={handleView}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                      />
                    ))
                  )}
                </div>
                <div className="pagination-footer" style={{ position: 'sticky', bottom: 0, flexShrink: 0 }}>
                  <div className="pagination-info" style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    Showing {pagedData.length} of {filteredData.length} items
                  </div>
                  <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <select
                      value={gridPageSize}
                      onChange={(e) => { setGridPageSize(Number(e.target.value)); setGridPage(0); }}
                      style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '1rem' }}
                    >
                      {[10, 25, 50, 100].map(size => (
                        <option key={size} value={size}>{size} / page</option>
                      ))}
                    </select>
                    <button className="page-btn" onClick={() => setGridPage(0)} disabled={safeGridPage === 0}>
                      <IconSystem.chevronLeft size={16} /><IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                    </button>
                    <button className="page-btn" onClick={() => setGridPage(safeGridPage - 1)} disabled={safeGridPage === 0}>
                      <IconSystem.chevronLeft size={16} />
                    </button>
                    <span style={{ padding: '0 0.75rem', fontSize: '0.85rem' }}>
                      Page {safeGridPage + 1} of {totalPages}
                    </span>
                    <button className="page-btn" onClick={() => setGridPage(safeGridPage + 1)} disabled={safeGridPage >= totalPages - 1}>
                      <IconSystem.chevronRight size={16} />
                    </button>
                    <button className="page-btn" onClick={() => setGridPage(totalPages - 1)} disabled={safeGridPage >= totalPages - 1}>
                      <IconSystem.chevronRight size={16} /><IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* v2.0 - CALENDAR VIEW (DISABLED MESSAGE) */}
          {viewMode === 'calendar' && (
            <div style={{
              padding: '3rem',
              textAlign: 'center',
              color: 'var(--text-muted)'
            }}>
              <IconSystem.calendar size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
              <h3 style={{ color: '#64748b', marginBottom: '0.5rem' }}>Calendar View Not Applicable</h3>
              <p>Calendar view is not available for Service Master</p>
            </div>
          )}
        </div>

        {/* Filter Panel */}
        {filterOpen && <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button
              onClick={() => { setCodeFilter(''); setNameFilter(''); setDescriptionFilter(''); setCategoryFilter(''); setUomFilter(''); setStatusFilter(''); setEntityFilter([]); setEffectiveFromFilter(''); setEffectiveToFilter(''); setGlAccountFilter(''); setFilterOpen(false); }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                color: 'var(--text-muted)',
                borderRadius: '0.25rem',
              }}
              title="Close filters"
            >
              <IconSystem.x size={16} />
            </button>
          </div>

          <div className="search-panel-content">
            {/* Code Filter */}
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Code</label>
              <input
                type="text"
                className="form-input"
                value={codeFilter}
                onChange={(e) => setCodeFilter(e.target.value)}
                placeholder="Filter by code..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            {/* Service Name Filter */}
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Service Name</label>
              <input
                type="text"
                className="form-input"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Filter by name..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            {/* Description Filter */}
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Description</label>
              <input
                type="text"
                className="form-input"
                value={descriptionFilter}
                onChange={(e) => setDescriptionFilter(e.target.value)}
                placeholder="Filter by description..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            {/* Category Filter */}
            <div className="filter-section">
              <SyutSelect
                label="Category"
                options={[
                  { value: '', label: 'All Categories' },
                  ...sampleCategories.map(c => ({ value: c.name, label: c.name }))
                ]}
                value={categoryFilter}
                onChange={(val) => setCategoryFilter((val as string) || '')}
                placeholder="Select category..."
                isClearable={false}
                size="sm"
              />
            </div>

            {/* UOM Filter */}
            <div className="filter-section">
              <SyutSelect
                label="UOM"
                options={[
                  { value: '', label: 'All UOMs' },
                  ...sampleUOMs.map(u => ({ value: u, label: u }))
                ]}
                value={uomFilter}
                onChange={(val) => setUomFilter((val as string) || '')}
                placeholder="Select UOM..."
                isClearable={false}
                size="sm"
              />
            </div>

            {/* Status Filter */}
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'Active', label: 'Active' },
                  { value: 'Inactive', label: 'Inactive' }
                ]}
                value={statusFilter}
                onChange={(val) => setStatusFilter((val as string) || '')}
                placeholder="Select status..."
                isClearable={false}
                size="sm"
              />
            </div>

            {/* BU / Entity Filter */}
            <div className="filter-section">
              <SyutSelect
                label="BU / Entity"
                isMulti
                variant="checkbox"
                options={sampleEntities.map(e => ({ value: e.id, label: `${e.code} - ${e.name}` }))}
                value={entityFilter}
                onChange={(val) => setEntityFilter(val as string[])}
                placeholder="Select entities..."
                showSelectAll
                size="sm"
              />
            </div>

            {/* Effective From Filter */}
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Effective From</label>
              <input
                type="date"
                className="form-input"
                value={effectiveFromFilter}
                onChange={(e) => setEffectiveFromFilter(e.target.value)}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            {/* Effective To Filter */}
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Effective To</label>
              <input
                type="date"
                className="form-input"
                value={effectiveToFilter}
                onChange={(e) => setEffectiveToFilter(e.target.value)}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            {/* GL Account Filter */}
            <div className="filter-section">
              <SyutSelect
                label="GL Account"
                options={[
                  { value: '', label: 'All GL Accounts' },
                  ...sampleGLAccounts.map(gl => ({ value: gl.id, label: `${gl.code} - ${gl.name}` }))
                ]}
                value={glAccountFilter}
                onChange={(val) => setGlAccountFilter((val as string) || '')}
                placeholder="Select GL account..."
                isClearable={false}
                size="sm"
              />
            </div>
          </div>

          <div className="filter-actions">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setCodeFilter('');
                setNameFilter('');
                setDescriptionFilter('');
                setCategoryFilter('');
                setUomFilter('');
                setStatusFilter('');
                setEntityFilter([]);
                setEffectiveFromFilter('');
                setEffectiveToFilter('');
                setGlAccountFilter('');
              }}
            >
              Clear
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setFilterOpen(false)}
            >
              Apply
            </button>
          </div>
        </div>}
      </div>

      {/* SERVICE FORM MODAL */}
      <SampleMasterFormModal
        isOpen={formModalOpen}
        mode={formMode}
        service={selectedService}
        onSave={() => {}}
        onClose={() => setFormModalOpen(false)}
        categories={sampleCategories}
        entities={sampleEntities}
        glAccounts={sampleGLAccounts}
        uoms={sampleUOMs}
        taxServices={sampleTaxServices}
        existingServices={sampleServices}
        openedBy="SampleMasterScreen"
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        copyInstanceId={copyInstanceId}
        minimizeRef={formModalMinimizeRef}
      />

      {/* VIEW MODAL */}
      <ServiceMasterViewModal
        isOpen={viewModalOpen}
        service={selectedService}
        onClose={() => setViewModalOpen(false)}
        onEdit={(service) => handleEdit(service.id)}
        glAccounts={sampleGLAccounts}
        entities={sampleEntities}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreView={handleRestoreView}
      />

      {/* DELETE CONFIRMATION MODAL */}
      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedService?.serviceName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={() => {
          setIsDeleting(true);
          setTimeout(() => {
            try {
              if (selectedService) {
                console.log('Delete:', selectedService.id);
                // In production: API call to delete
              }
              setIsDeleting(false);
              setDeleteModalOpen(false);
              notificationService.toast.success(`"${selectedService?.serviceName}" has been deleted successfully.`);
              setSelectedService(null);
            } catch {
              setIsDeleting(false);
              notificationService.toast.error('An error occurred while deleting. Please try again.');
            }
          }, 800);
        }}
        onCancel={() => setDeleteModalOpen(false)}
      />

      {/* Service Search Popup */}
      <SyutSearchGrid<ServiceMasterRecord>
        isOpen={searchPopupOpen}
        onClose={() => setSearchPopupOpen(false)}
        onSelect={(service) => {
          console.log('Selected service:', service);
          handleView(service.id);
        }}
        title="Search Services"
        columns={[
          { key: 'code', header: 'Code', width: '100px', sortable: true },
          { key: 'serviceName', header: 'Service Name', width: '200px', sortable: true },
          { key: 'category', header: 'Category', width: '150px', sortable: true },
          { key: 'uom', header: 'UOM', width: '80px', sortable: true },
          {
            key: 'status',
            header: 'Status',
            width: '100px',
            render: (value: string) => (
              <span style={{
                padding: '0.125rem 0.5rem',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: 500,
                background: value === 'Active' ? '#dcfce7' : '#fee2e2',
                color: value === 'Active' ? '#166534' : '#991b1b',
              }}>
                {value}
              </span>
            )
          },
        ]}
        data={sampleServices}
        filters={[
          {
            key: 'category',
            label: 'Category',
            type: 'select',
            options: sampleCategories.map(c => ({ value: c.name, label: c.name }))
          },
          {
            key: 'status',
            label: 'Status',
            type: 'select',
            options: [
              { value: 'Active', label: 'Active' },
              { value: 'Inactive', label: 'Inactive' }
            ]
          }
        ]}
        rowKey="id"
        enableRecentlyUsed
        recentlyUsedKey="service-master-search"
        searchPlaceholder="Search by code or name... (F2 to focus)"
      />
    </div>
  );
};

export default SampleMasterScreenTabbed;